import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

// Constants
import {
  ABOUT_PAGE_URL,
  CONTACT_PAGE_URL,
  GALLERY_PAGE_URL,
  HOME_PAGE_URL,
  MAINTENANCE_PAGE_URL,
  POLICIES_PAGE_URL,
  SESSIONS_PAGE_URL,
  INDEX_PAGE_URL,
  FANSITES_PAGE_URL,
  CLIPS_PAGE_URL,
  LINKS_PAGE_URL,
  MAILING_LIST_PAGE_URL,
  MAILING_LIST_CONFIRM_EMAIL_PAGE_URL,
  MAILING_LIST_UNSUBSCRIBE_PAGE_URL,
  MAILING_LIST_ADMIN_PAGE_URL,
  LOGIN_PAGE_URL,
  FAQ_PAGE_URL,
} from './utils/constants/Routes';

import {
  COOKIE_TIMER,
  COOKIE_POLICY_NAME,
} from './utils/constants/Constants';

// Components
import LoadingScreen from './components/utils/LoadingScreen';
import HomePage from './pages/home/HomePage';      // NOT LAZY
import IndexPage from './pages/index/IndexPage';  // NOT LAZY
import useScrollToTopOnRouteChange from './utils/functions/ScrollToTop';
import AboutPage from './pages/about/AboutPage';
import SessionsPage from './pages/sessions/SessionsPage';
import GalleryPage from './pages/gallery/GalleryPage';
import ClipsPage from './pages/clips/ClipsPage';
import FansitesPage from './pages/fansites/FansitesPage';
import ContactPage from './pages/contact/ContactPage';
import LinksPage from './pages/links/LinksPage';
import MailingSignUpPage from './pages/mailing/MailingSignUpPage';
import MailingValidationPage from './pages/mailing/MailingValidationPage';
import MailingUnsubscribePage from './pages/mailing/MailingUnsubscribePage';
import MailingAdminPage from './pages/mailing/admin/MailingAdminPage';
import TermAndPoliciesPage from './pages/policies/TermAndPoliciesPage';
import FaqPage from './pages/faq/FaqPage';

const MaintenancePage = lazy(() =>
  import('./pages/maintenance/MaintenancePage')
);
const LoginPage = lazy(() => import('./users/login/LoginPage'));
const Error404 = lazy(() => import('./pages/error/Error404'));

// Cookie Modal
const CookieConsentModal = lazy(() =>
  import('./components/modals/CookieConsentModal')
);

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAgreedToCookies, setHasAgreedToCookies] = useState(true);

  useScrollToTopOnRouteChange();

  // Load cookie state
  useEffect(() => {
    const cookie = localStorage.getItem(COOKIE_POLICY_NAME);
    setHasAgreedToCookies(!!cookie);
  }, []);

  // Timer for cookie modal
  useEffect(() => {
    if (hasAgreedToCookies) {
      setIsVisible(false);
    } else {
      const timer = setTimeout(() => setIsVisible(true), COOKIE_TIMER);
      return () => clearTimeout(timer);
    }
  }, [hasAgreedToCookies]);

  return (
    <>
      {isVisible && (
        <Suspense fallback={<LoadingScreen />}>
          <CookieConsentModal setHasAgreedToCookies={setHasAgreedToCookies} />
        </Suspense>
      )}

      {/* Route-level Suspense has NO visible fallback now */}
      <Suspense fallback={null}>
        <Routes>
          {/* NOT LAZY */}
          <Route path={INDEX_PAGE_URL} index element={<IndexPage />} />
          <Route path={HOME_PAGE_URL} element={<HomePage />} />

          {/* LAZY LOADED (preloaded in useEffect) */}
          <Route path={ABOUT_PAGE_URL} element={<AboutPage />} />
          <Route path={SESSIONS_PAGE_URL} element={<SessionsPage />} />
          <Route path={GALLERY_PAGE_URL} element={<GalleryPage />} />
          <Route path={CLIPS_PAGE_URL} element={<ClipsPage />} />
          <Route path={FANSITES_PAGE_URL} element={<FansitesPage />} />
          <Route path={CONTACT_PAGE_URL} element={<ContactPage />} />
          <Route path={LINKS_PAGE_URL} element={<LinksPage />} />
          <Route path={FAQ_PAGE_URL} element={<FaqPage />} />

          {/* Mailing */}
          <Route
            path={MAILING_LIST_PAGE_URL}
            element={<MailingSignUpPage />}
          />
          <Route
            path={`${MAILING_LIST_CONFIRM_EMAIL_PAGE_URL}/:userId/:uniqueString/:verificationTokenId`}
            element={<MailingValidationPage />}
          />
          <Route
            path={MAILING_LIST_UNSUBSCRIBE_PAGE_URL}
            element={<MailingUnsubscribePage />}
          />
          <Route
            path={MAILING_LIST_ADMIN_PAGE_URL}
            element={<MailingAdminPage />}
          />

          {/* Policies & System */}
          <Route path={POLICIES_PAGE_URL} element={<TermAndPoliciesPage />} />
          <Route path={MAINTENANCE_PAGE_URL} element={<MaintenancePage />} />
          <Route path={LOGIN_PAGE_URL} element={<LoginPage />} />

          {/* 404 */}
          <Route path="*" element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
